'use client'

import React from 'react'
import useSwr from 'swr'
import Wallet from '@/utils/wallet'
import { fetcher } from '@/utils/fetcher'
import { API_URL } from '@/utils/constants'
import featureFlags from '@/utils/feature-flags'
import { useAdminCheck } from '@/utils/hooks/useAdminMetrics'
import SplitPromptDialog from './dialog'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import {
	claimDailyMeasure,
	isSnoozed,
	markSplitFromPrompt,
	measureUtxos,
	recordSplitPrompt,
	shouldPromptSplit,
	snooze,
	SPLIT_PROMPT_FLAG,
	isSplitPromptTester,
	markSeen,
	wasSeenLocally,
	wasSeenRemotely,
	thresholdSats
} from '@/utils/split-prompt'

/**
 * Measures the connected wallet's spendable UTXOs on the wallet page (every
 * wallet, once a day) and, when the bitcoin sits in one UTXO worth $100 or
 * more, offers to split it. Shown to admins and to everyone once `wallet::split_prompt` is on;
 * `?split_prompt=1` forces it for testing.
 */
export default function SplitPrompt() {
	const address = React.useMemo(() => {
		try {
			return Wallet.balanceAddress() || null
		} catch {
			return null
		}
	}, [])
	const taproot = React.useMemo(() => {
		try {
			return Wallet.getTaprootAddress() || null
		} catch {
			return null
		}
	}, [])
	const walletType = React.useMemo(() => {
		try {
			return String(Wallet.type() || '')
		} catch {
			return ''
		}
	}, [])
	// Same key as the UTXO manager, so opening it after the prompt is instant.
	const { data } = useSwr(address ? `${API_URL}/wallet/${address}/all-utxos` : null, fetcher, {
		revalidateOnFocus: false
	})
	const { isAdmin } = useAdminCheck(taproot)
	const { exchangeRate } = useExchangeRate()
	const shape = React.useMemo(() => (data ? measureUtxos(data.utxos) : null), [data])
	const [open, setOpen] = React.useState(false)
	const shownRef = React.useRef(false)

	React.useEffect(() => {
		if (!address || !shape) return
		if (claimDailyMeasure(address)) {
			recordSplitPrompt('measured', address, shape, { wallet: walletType })
		}
	}, [address, shape, walletType])

	React.useEffect(() => {
		if (!address || !shape || shownRef.current) return
		// Only the explicit test link reopens it; everyone else sees it once.
		const testLink =
			typeof window !== 'undefined' &&
			new URLSearchParams(window.location.search).get('split_prompt') === '1'
		const tester = isSplitPromptTester(taproot)
		const enabled = testLink || tester || isAdmin || featureFlags(SPLIT_PROMPT_FLAG)
		if (!enabled) return
		// Testers and the test link open on any spendable UTXO, whatever its
		// size, so the flow can be tried without $100 in one piece.
		const sizeOk =
			testLink || tester
				? shape.largestSats > 0
				: shouldPromptSplit(shape, thresholdSats(exchangeRate))
		if (!sizeOk) return
		if (!testLink && (wasSeenLocally(address) || isSnoozed(address))) return
		shownRef.current = true
		let cancelled = false
		void (async () => {
			if (!testLink && (await wasSeenRemotely(address))) {
				markSeen(address)
				return
			}
			if (cancelled) return
			markSeen(address)
			setOpen(true)
			recordSplitPrompt('shown', address, shape, { wallet: walletType })
		})()
		return () => {
			cancelled = true
		}
	}, [address, shape, isAdmin, walletType, exchangeRate, taproot])

	if (!address || !shape) return null

	// The prompt is the split tool: a split made here is the prompt's split.
	const interact = () => {
		recordSplitPrompt('clicked', address, shape, { wallet: walletType })
		markSplitFromPrompt(address)
	}
	const done = () => setOpen(false)
	const later = () => {
		recordSplitPrompt('dismissed', address, shape, { wallet: walletType })
		snooze(address)
		setOpen(false)
	}

	return (
		<SplitPromptDialog
			open={open}
			largestSats={shape.largestSats}
			btcUsd={exchangeRate}
			onInteract={interact}
			onDone={done}
			onLater={later}
		/>
	)
}
